import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  message,
  Tag,
  Row,
  Col,
  List,
  notification,
  Select,
  Modal,
} from 'antd';
import { useCommandSocket } from '../../../../../../Hooks/WebSocket/useCommandSocket';
import { useSDK } from '../../../../../../Hooks/TT_ELD/Mobile/SDK';

const { Text } = Typography;

interface DeviceCommandCardProps {
  driverUid: string;
}

const EldCommand: React.FC<DeviceCommandCardProps> = ({ driverUid }) => {
  const { eldResponses, sendCommand, isConnected, infos, errors, driverStatus } = useCommandSocket({
    driverUid,
  });

  const [textValue, setTextValue] = useState('');
  const [loading, setLoading] = useState(false);

  const { data } = useSDK('MCU', {
    is_active: true,
  });
  const { data: bleData } = useSDK('BLE', {
    is_active: true,
  });

  useEffect(() => {
    if (infos.length === 0) return;
    const latestInfo = infos[0];
    const msg =
      typeof latestInfo === 'string' ? latestInfo : latestInfo?.message || 'Info received';
    notification.info({
      message: 'Info',
      description: msg,
      placement: 'topRight',
      duration: 5,
    });
  }, [infos]);

  useEffect(() => {
    if (errors.length === 0) return;
    const latestError = errors[0];
    const msg =
      typeof latestError === 'string' ? latestError : latestError?.message || 'Error occurred';
    notification.error({
      message: 'Error',
      description: msg,
      placement: 'topRight',
      duration: 5,
    });
  }, [errors]);

  const handleSend = (command: string, text: string) => {
    if (!isConnected) return message.error('Socket not connected!');
    if (!command) return;

    setLoading(true);
    sendCommand(command, text.trim());
    message.info('Command sent');
    setTimeout(() => setLoading(false), 1200);
  };

  const handleManualSend = () => handleSend('eld_config', textValue);
  const handleManualSelect = (val: string) => {
    if (!val) return;

    Modal.confirm({
      title: 'Confirm?',
      content: `You want to send the command "${val}"`,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: () => {
        handleSend('app_command', val);
      },
    });
  };

  const options =
    data?.data
      ?.filter((item: any) => item.is_supported)
      .map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? [];

  const BLEoptions =
    bleData?.data
      ?.filter((item: any) => item.is_supported)
      .map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? [];

  const bgUpdate = (val: string) => {
    if (!val) return;

    const selectedObj = data?.data.find((item: any) => item.id === val);
    if (!selectedObj) return;

    Modal.confirm({
      title: 'Confirm Firmware Update',
      content: `Are you sure you want to send the "${selectedObj.name}" command with the selected firmware?`,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: () => {
        handleSend('protrux_bg_update', JSON.stringify(selectedObj));
      },
    });
  };
  const bgBleUpdate = (val: string) => {
    if (!val) return;

    const selectedObj = bleData?.data.find((item: any) => item.id === val);
    if (!selectedObj) return;

    Modal.confirm({
      title: 'Confirm Bluetooth Update',
      content: `Are you sure you want to send the "${selectedObj.name}" command with the selected bluetooth?`,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: () => {
        handleSend('protrux_bg_update', JSON.stringify(selectedObj));
      },
    });
  };

  const handleStart = () => handleSend('start_eld_data', '');
  const handleStop = () => handleSend('stop_eld_data', '');

  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            ELD Command
          </Text>
          <Tag color={isConnected ? 'green' : 'red'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Tag>
          <Tag color={driverStatus === 'on' ? 'green' : driverStatus === 'off' ? 'red' : 'default'}>
            {driverStatus
              ? driverStatus === 'on'
                ? 'Driver socket connect'
                : 'Driver socket disconnect'
              : 'Unknown'}
          </Tag>
        </div>
      }
      style={{
        borderRadius: 14,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        padding: 8,
      }}
    >
      {/* Command controls */}

      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} lg={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select firmware"
            allowClear
            onSelect={(val) => {
              bgUpdate(val);
            }}
            options={options}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select bluetooth"
            allowClear
            onSelect={(val) => {
              bgBleUpdate(val);
            }}
            options={BLEoptions}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select command"
            allowClear
            onSelect={(val) => handleManualSelect(val)}
            options={[
              { label: 'Get Screenshot', value: 'getScreenshot' },
              { label: 'Get Summary', value: 'getSummary' },
            ]}
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} lg={8}>
          <Input
            placeholder="ELD Command"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onPressEnter={handleManualSend}
          />
        </Col>

        <Col xs={24} lg={4}>
          <Button
            type="primary"
            block
            onClick={handleManualSend}
            loading={loading}
            disabled={!textValue}
          >
            Send
          </Button>
        </Col>
        <Col xs={24} lg={4}>
          <Button block type="default" onClick={handleStart} disabled={!isConnected}>
            Start ELD Data
          </Button>
        </Col>

        <Col xs={24} lg={4}>
          <Button block type="default" onClick={handleStop} disabled={!isConnected}>
            Stop ELD Data
          </Button>
        </Col>
      </Row>

      {/* Response list */}
      <div style={{ marginTop: 20 }}>
        {eldResponses.length > 0 ? (
          <Card
            size="small"
            title={<Text strong>Device Responses</Text>}
            style={{
              borderRadius: 10,
              maxHeight: 400,
              overflowY: 'auto',
            }}
            styles={{ body: { padding: '10px 14px' } }}
          >
            <List
              dataSource={eldResponses}
              renderItem={(item, index) => {
                let parsedText = item.text;

                try {
                  parsedText = JSON.parse(item.text);
                } catch (e) {}

                const isBase64 = item.command === 'app_command_response' && item.type === 'base64';

                return (
                  <List.Item
                    key={index}
                    style={{
                      borderRadius: 8,
                      marginBottom: 10,
                      padding: '10px 14px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#1677ff',
                        marginBottom: 6,
                      }}
                    >
                      Response #{eldResponses.length - index}
                    </div>

                    {isBase64 ? (
                      <img
                        src={`data:image/png;base64,${parsedText}`}
                        alt="device-img"
                        style={{
                          maxHeight: '350px',
                          borderRadius: 6,
                          marginTop: 6,
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          const newWindow = window.open();
                          if (newWindow) {
                            newWindow.document.write(
                              `<html style="height: 100%;"><head><meta name="viewport" content="width=device-width, minimum-scale=0.1"><title>Device image</title></head><body style="margin: 0px; height: 100%; background-color: rgb(14, 14, 14);"><img style="display: block;-webkit-user-select: none;margin: auto;cursor: zoom-in;background-color: hsl(0, 0%, 90%);transition: background-color 300ms; max-width:100% ; max-height:100vh" src="data:image/png;base64,${parsedText}"></body></html>`,
                            );
                            newWindow.focus();
                          }
                        }}
                      />
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof parsedText === 'string'
                          ? parsedText
                          : JSON.stringify(parsedText, null, 2)}
                      </pre>
                    )}
                  </List.Item>
                );
              }}
            />
          </Card>
        ) : (
          <Text
            type="secondary"
            style={{
              display: 'block',
              textAlign: 'center',
              marginTop: 10,
            }}
          >
            No response yet
          </Text>
        )}
      </div>
    </Card>
  );
};

export default EldCommand;
